import React from 'react';
import { Card, CardContent, Box, Typography, Button } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  TrendingDown as DowngradeIcon,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';
import PlanFeatureList from './PlanFeatureList';
import { buildBasicPlanFeatures } from '../utils/planFeatures';
import { formatMoney } from '../utils/money';

/**
 * The Basic (free, pay-as-you-go) plan, as a fourth card in the plan grid.
 *
 * It was a line of text under the grid, then a named card at the bottom. Being
 * a CARD IN THE GRID is the point: pay-as-you-go is one of the four things a
 * customer can be on, and anything outside the row of plans reads as a footnote
 * to the three that are in it. Priced columns compare; a paragraph does not.
 *
 * ── WHY NOT PlanCard ────────────────────────────────────────────────────────
 * PlanCard is built around a subscription: a monthly or yearly price, an
 * upgrade/downgrade relationship, and one CTA that buys it. Basic has no price
 * to show, is never an "upgrade", and carries TWO actions. Threading a
 * variant through PlanCard would put a branch on nearly every line of it and
 * leave both cards harder to read than two files.
 *
 * ── …WHICH IS NOT A LICENCE TO LOOK DIFFERENT ───────────────────────────────
 * Being a separate file cost this card its resemblance to the three beside it.
 * It had hardcoded 24px/14px type where the others take `variant="h5"` and
 * `variant="body2"` — and this app's theme scales those DOWN to 1.125rem and
 * 0.75rem, so Basic's heading and description rendered about a third larger
 * than its neighbours'. It also had a wider corner radius, no shadow, no hover
 * lift, and its own slightly different green.
 *
 * So every shared surface here now takes its value from PlanCard rather than
 * from a number typed into this file: same variants, same radius, same border,
 * same shadow and hover, same greens and greys, same price slot. What differs
 * below differs because Basic IS different — no price, two actions — not
 * because the two files drifted.
 *
 * What they must share structurally, they do share: PlanFeatureList draws the
 * ticked rows, and planFeatures.js builds them from one list of common claims
 * — so a change to a shared feature cannot land on three cards and miss the
 * fourth.
 */

// Lifted from PlanCard so the four cards are one palette. ORANGE is the price
// colour on the paid cards and is used for the same slot here; GREEN and the
// greys are its current-plan and downgrade tones.
const ORANGE = '#FF7A59';
const GREEN = '#006A63';
const GREEN_DARK = '#004F4A';
const GREEN_TINT = 'rgba(0,106,99,0.06)';
const GREY_TEXT = '#8A8F85';
const GREY_BORDER = '#DDE0D8';

/** What the primary button says and does, from the account's actual state. */
export const BASIC_PLAN_ACTION = {
  DOWNGRADE: 'downgrade',
  CURRENT: 'current',
  DEFAULT: 'default',
};

/**
 * Which of the three the account is in.
 *
 * There is deliberately no cancel state: Basic is what an account falls back
 * TO, so there is never anything here to cancel.
 *
 * @param hasPaidSubscription  a live paid plan is in force
 * @param isBasicActive        the account is on Basic right now
 */
export function basicPlanAction({ hasPaidSubscription, isBasicActive } = {}) {
  if (hasPaidSubscription) return BASIC_PLAN_ACTION.DOWNGRADE;
  if (isBasicActive) return BASIC_PLAN_ACTION.CURRENT;
  // No subscription and not yet on Basic — a trial. Basic is where the account
  // lands when the trial ends, so it is stated as the default rather than
  // offered as a change.
  return BASIC_PLAN_ACTION.DEFAULT;
}

const BUTTON_TEXT = {
  [BASIC_PLAN_ACTION.DOWNGRADE]: 'Downgrade',
  [BASIC_PLAN_ACTION.CURRENT]: 'Current plan',
  [BASIC_PLAN_ACTION.DEFAULT]: 'Default plan',
};

// White button, coloured text + outline — PlanCard's helper, and the shared
// shape for every button on the row except an emphasised filled CTA.
const outlinedIn = (color, borderColor = color, hoverTint = 'rgba(0,0,0,0.03)') => ({
  bgcolor: '#FFFFFF',
  borderColor,
  color,
  '&:hover': { borderColor, bgcolor: hoverTint },
});

/**
 * Card chrome, identical for both layouts.
 *
 * Radius, border, shadow and hover: PlanCard's neutral (non-popular,
 * non-current) card, to the value. Basic is never popular and never outlined
 * green — it is the plan you are on by default rather than the one being sold —
 * so the neutral treatment is the whole of it.
 *
 * REQUESTED for the wide row: "match the styling with the existing cards".
 * Shared from one object rather than copied into the second layout, so the two
 * cannot drift into two slightly different cards.
 */
const CARD_SX = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: '#eee',
  position: 'relative',
  overflow: 'visible',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
  },
};

const BasicPlanCard = ({
  packages = [],
  onDowngrade,
  onBuyCreditPacks,
  creditPacksReady = false,
  hasPaidSubscription = false,
  isBasicActive = false,
  disabled = false,
  // 'month' | 'year'. Basic costs nothing either way; this follows the toggle
  // above the grid only so the four price rows read as one row. A card still
  // saying "/Month" while its neighbours say "/Year" looks like a stale card.
  interval = 'month',
  /**
   * 'vertical' — a column card, the shape it has beside the paid tiers.
   * 'horizontal' — REQUESTED: one full-width row beneath them, kept short:
   *   the name and price on the left and the buttons on the right, with the
   *   features in columns between them when the card is wide enough, or
   *   beneath both when it is not. See the note on the wide row below.
   *
   * Only the ARRANGEMENT differs. Every piece below — the name, the
   * description, the price slot, the feature rows, both buttons and all of
   * their states — is built once and placed by whichever layout is asked for,
   * because the request is explicit that nothing about the content changes.
   *
   * 'horizontal' arranges itself by the CARD's width, not the screen's: one
   * row when there is room, name and buttons above the features when there is
   * less, and stacked in the column card's order when narrow. There is no
   * honest way to put three feature columns and a button beside a price in a
   * phone's width.
   */
  layout = 'vertical',
}) => {
  const features = buildBasicPlanFeatures({
    packages,
    // The same formatter the pack picker and the checkout use. A price that
    // reads "$5" here and "$5.00" there looks like two different prices.
    formatPrice: (cents) => formatMoney(cents, 'usd'),
  });

  const action = basicPlanAction({ hasPaidSubscription, isBasicActive });
  const isDowngrade = action === BASIC_PLAN_ACTION.DOWNGRADE;

  // Buying packs is offered where it is the thing to do: on Basic, and on a
  // trial that is about to become Basic. A paid subscriber tops up from their
  // own plan card, and offering it here would read as part of downgrading.
  // …and only when the host actually wired somewhere for it to go. A button
  // that opens nothing is worse than an absent one: it reads as broken rather
  // than as unavailable.
  const showBuyPacks = !hasPaidSubscription && Boolean(onBuyCreditPacks);

  const isHorizontal = layout === 'horizontal';

  // ── The pieces ────────────────────────────────────────────────────────────
  // Built once and placed by whichever layout is asked for. The request is
  // explicit that only the arrangement changes, so there is deliberately no
  // second copy of any of this for the wide row to drift away from.

  const identity = (
    <>
      {/* Plan name — the variant, not a pixel size. The theme scales h5 down
          to 1.125rem, so a hardcoded 24px here stood a third taller than
          "Grow", "Optimize" and "Scale" beside it. */}
      <Typography component="h3" variant="h5" fontWeight={700} gutterBottom>
        Basic
      </Typography>

      {/* Description — same variant, colour and minHeight as the other three,
          so all four descriptions occupy one band and the prices under them
          line up whether the copy runs to one line or two.

          The minHeight is a COLUMN concern: it reserves a second line so four
          side-by-side cards stay in step. In the wide row there is nothing
          beside this to stay in step with, and the reserved line would just be
          a gap above the price. */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2.5, minHeight: isHorizontal ? 0 : 40 }}
      >
        Best if you only need fresh local SEO content every now and then.
      </Typography>

      {/* ── The price slot ────────────────────────────────────────────────
          Same slot and same shape as the paid cards: the figure in ORANGE at
          2.2rem, the interval muted beside it, and one small green line
          beneath — the position those cards use for "Save $X a year". The
          subscription price really is zero, and the green line says what
          that does and does not mean before the eye leaves the block.

          Previously this was two lines of 22px green, which was the largest
          type on the row and pushed every feature below it out of step with
          the neighbouring cards. */}
      <Box sx={{ mb: isHorizontal ? 0 : 2.5 }}>
        <Typography
          component="span"
          sx={{ fontSize: '2.2rem', fontWeight: 700, color: ORANGE }}
        >
          $0.00
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: '0.95rem', color: 'text.secondary', ml: 0.3 }}
        >
          {interval === 'year' ? '/Year' : '/Month'}
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: GREEN, mt: 0.5 }}>
          <Box component="span">No monthly charge.</Box>{' '}
          <Box component="span">Pay as you go.</Box>
        </Typography>
      </Box>
    </>
  );

  const actions = (
    <>
      {/* Primary: what this card is, or the one action it offers.
          Current and Default are STATES, not actions, so they are disabled —
          with explicit Mui-disabled styling, or MUI greys the green away. */}
      <Button
        fullWidth
        size="large"
        variant="outlined"
        disabled={!isDowngrade || disabled}
        onClick={isDowngrade ? onDowngrade : undefined}
        startIcon={isDowngrade ? <DowngradeIcon /> : <CheckIcon />}
        sx={{
          py: 1.4,
          borderRadius: 2.5,
          fontWeight: 700,
          fontSize: '0.95rem',
          textTransform: 'none',
          boxShadow: 'none',
          // Downgrade is the quietest treatment on the card: a real action,
          // never the one being encouraged. Both branches are PlanCard's, so
          // "Current plan" here and "Current Plan" there are the same button.
          ...(isDowngrade
            ? outlinedIn(GREY_TEXT, GREY_BORDER)
            : {
              ...outlinedIn(GREEN, GREEN, GREEN_TINT),
              '&.Mui-disabled': {
                bgcolor: '#FFFFFF',
                borderColor: GREEN,
                color: GREEN,
                opacity: 0.9,
              },
            }),
        }}
      >
        {BUTTON_TEXT[action]}
      </Button>

      {/* Secondary: the actual pay-as-you-go action. Filled and gradient,
          matching the emphasised orange CTA on the paid cards — in green,
          because this is the free plan's own call to action rather than a
          competitor to the upgrade above it. It sits where PlanCard puts its
          second button, at the same 1.25 gap. */}
      {showBuyPacks && (
        <Button
          fullWidth
          size="large"
          variant="contained"
          disabled={!creditPacksReady || disabled}
          onClick={onBuyCreditPacks}
          startIcon={<CartIcon />}
          sx={{
            mt: 1.25,
            py: 1.4,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: '0.95rem',
            textTransform: 'none',
            color: '#FFFFFF',
            background: `linear-gradient(135deg, ${GREEN_DARK} 0%, ${GREEN} 100%)`,
            boxShadow: '0 4px 14px rgba(0, 106, 99, 0.35)',
            '&:hover': {
              background: `linear-gradient(135deg, #003D39 0%, ${GREEN_DARK} 100%)`,
              boxShadow: '0 6px 20px rgba(0, 106, 99, 0.45)',
            },
          }}
        >
          Buy credit pack
        </Button>
      )}
    </>
  );

  // ── The wide row ──────────────────────────────────────────────────────────
  // REQUESTED: below the three plans, spanning the full container, with the
  // details unchanged — and SHORT. The full width only earns its place if the
  // features spread across it; a tall card across the whole row is the failure
  // this layout was reworked to remove. It stacks back into the column order
  // when narrow.
  if (isHorizontal) {
    return (
      <Card
        data-testid="basic-plan-card"
        // The card is its own size container, so the arrangement inside follows
        // the CARD's width rather than the viewport's. See below for why.
        sx={{ ...CARD_SX, containerType: 'inline-size' }}
      >
        {/* ── Arranged by the card's width, not the screen's ─────────────────
            The page grid switches at a 768px VIEWPORT, as specified. But
            Dashboard's sidebar is a fixed 256px at every width, so the viewport
            says little about how much room this card really has.

            Three arrangements, each the SHORTEST that width allows:
              ≥ 1150px  name/price │ features │ buttons   — one row
              ≥ 560px   name/price           buttons
                        features, up to four columns beneath
              < 560px   stacked, the same order as the column card

            The one-row arrangement starts at 1150px because that is where the
            features, between the two fixed side columns, get three columns
            (3 × 180px and two gaps). It used to start at 900px, where they got
            ONE: the card came out as a tall single column of features with the
            name and buttons floating at its middle. Below 1150px the features
            sit beneath instead, with the card's whole width to spread across.

            OLD (briefly): the card was capped at 760px and centred to make it
            smaller. REQUESTED instead: keep the full width, make it shorter.

            Grid areas rather than reordering elements: the DOM stays name →
            features → buttons in every arrangement, so a screen reader hears
            the card in the same order as the vertical one. */}
        <Box
          sx={{
            p: 3,
            display: 'grid',
            gap: 3,
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateAreas: '"identity" "features" "actions"',
            '@container (min-width: 560px)': {
              gridTemplateColumns: 'minmax(0, 1fr) 220px',
              gridTemplateAreas: '"identity actions" "features features"',
              // Top-aligned, so every block starts on one line — the plan name,
              // the first feature and the first button — the way each paid card
              // opens with its name at the top. It carries into the one-row
              // arrangement too.
              // OLD: alignItems: 'center', which floated "Basic" and the buttons
              // down to the middle of the feature grid, so the name no longer
              // read as the card's heading.
              alignItems: 'start',
            },
            '@container (min-width: 1150px)': {
              gridTemplateColumns: '230px minmax(0, 1fr) 220px',
              gridTemplateAreas: '"identity features actions"',
              columnGap: 4,
            },
          }}
        >
          <Box sx={{ gridArea: 'identity' }}>
            {identity}
          </Box>

          {/* minWidth:0 — without it a long feature label sets this block's
              min-content width and it refuses to shrink, pushing the buttons
              off the end of the row.

              Up to FOUR columns: across the full row the list has room for more
              than three, and every column it fits is a row off the card's
              height. PlanFeatureList drops columns on its own when they would
              fall below a readable width. */}
          <Box sx={{ gridArea: 'features', minWidth: 0 }}>
            <PlanFeatureList features={features} columns={4} />
          </Box>

          <Box sx={{ gridArea: 'actions' }}>
            {actions}
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      data-testid="basic-plan-card"
      sx={{
        ...CARD_SX,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {identity}
        <PlanFeatureList features={features} />
      </CardContent>

      <Box sx={{ p: 3, pt: 0 }}>
        {actions}
      </Box>
    </Card>
  );
};

export default BasicPlanCard;
